import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import * as React from 'react'

import type {BuildId} from '../../../../../types'
import {formatDuration} from '../../../../../utils/dateTimeUtils'
import Popup from '../../../../common/Popup/Popup.lazy'
import type {FullLogTarget, TimelineStageStatus, PointType} from '../../BuildLog.types'

import TimelinePoint from './TimelinePoint/TimelinePoint.container'
import TimelineStageButton from './TimelineStageButton/TimelineStageButton.container'

import styles from './TimelineStage.css'

const POPUP_DIRECTIONS = [Directions.BOTTOM_RIGHT, Directions.BOTTOM_LEFT]
const POPUP_TOP_MARGIN = -24
const POPUP_LEFT_MARGIN = -2
const MIN_PERCENT_TO_SHOW_FULL_LABEL = 10
const ONE_SECOND = 1000
const ONE_HUNDRED_PERCENT = 100
const ROUND_PERCENT_PARAM = 100
type Props = {
  stageKey: string
  name: string
  start: number
  end: number
  // Milliseconds
  duration: number
  running: boolean
  stepNumber: number | null | undefined
  nowX: number | null | undefined
  viewPointPercent: number | null | undefined
  buildId: BuildId
  pointName: string | null | undefined
  pointBuildAnchor: number | null | undefined
  pointType: PointType | undefined
  pointX: number | null | undefined
  lastKnownStatus: TimelineStageStatus | null | undefined
  fisrtStatus: TimelineStageStatus | null | undefined
  secondStatus: TimelineStageStatus | null | undefined
  isFinish: boolean
  logTarget: FullLogTarget
}
type State = {
  pointHovered: boolean
}

class TimelineStage extends React.PureComponent<Props, State> {
  state: State = {
    pointHovered: false,
  }

  onPointMouseEnter: () => void = () => {
    this.setState({
      pointHovered: true,
    })
  }

  onPointMouseLeave: () => void = () => {
    this.setState({
      pointHovered: false,
    })
  }

  render(): React.ReactNode {
    const {
      name,
      start,
      end,
      duration,
      running,
      stepNumber,
      nowX,
      buildId,
      viewPointPercent,
      pointName,
      pointBuildAnchor,
      pointType,
      pointX,
      lastKnownStatus,
      fisrtStatus,
      secondStatus,
      isFinish,
      stageKey,
      logTarget,
    } = this.props
    const {pointHovered} = this.state
    const shortLabel = stepNumber != null && stepNumber > 0 ? stepNumber.toString() : null
    const fullLabel = (
      <>
        {shortLabel != null ? `${shortLabel}: ${name}` : name}
        <span data-test-timilene-stage={'duration'} className={styles.duration}>{` ${formatDuration(
          Math.round(duration / ONE_SECOND),
          {
            alwaysShowSeconds: true,
          },
        )}`}</span>
      </>
    )

    const firstPartLineClass = classNames({
      [styles.successfulLine]: fisrtStatus === 'SUCCESSFUL',
      [styles.failedLine]: fisrtStatus === 'FAILED',
      [styles.canceledLine]: fisrtStatus === 'CANCELED',
    })
    const secondPartLineClass = classNames({
      [styles.successfulLine]: secondStatus === 'SUCCESSFUL',
      [styles.failedLine]: secondStatus === 'FAILED',
      [styles.canceledLine]: secondStatus === 'CANCELED',
    })
    const labelClass = classNames({
      [styles.successfulLabel]: lastKnownStatus === 'SUCCESSFUL',
      [styles.failedLabel]: lastKnownStatus === 'FAILED',
      [styles.canceledLabel]: lastKnownStatus === 'CANCELED',
    })
    const durationPercent = end + 1 - start
    const pointPercent =
      pointX != null
        ? Math.round(
            (((pointX - start) * ONE_HUNDRED_PERCENT) / durationPercent) * ROUND_PERCENT_PARAM,
          ) / ROUND_PERCENT_PARAM
        : null
    const nowPercent =
      running && nowX != null
        ? Math.round(
            (((nowX - start) * ONE_HUNDRED_PERCENT) / durationPercent) * ROUND_PERCENT_PARAM,
          ) / ROUND_PERCENT_PARAM
        : null
    const anchor = (
      <div className={styles.anchor}>
        <TimelineStageButton
          stageKey={stageKey}
          buildId={buildId}
          className={styles.action}
          logTarget={logTarget}
          aria-label={name}
        >
          <span className={classNames(styles.separator, firstPartLineClass)} />
          <span
            className={classNames(styles.stageLine, firstPartLineClass)}
            style={
              pointPercent != null || nowPercent != null
                ? {
                    width: `${pointPercent ?? nowPercent ?? ONE_HUNDRED_PERCENT}%`,
                  }
                : undefined
            }
          />
          {isFinish && (
            <span
              className={classNames(
                styles.separator,
                styles.separatorFinish,
                secondPartLineClass || firstPartLineClass,
              )}
            />
          )}
          <span
            className={classNames(styles.shortLabel, labelClass)}
            data-test-timeline-stage={'label'}
          >
            {durationPercent > MIN_PERCENT_TO_SHOW_FULL_LABEL ? fullLabel : shortLabel}
          </span>
          {pointPercent != null && (nowPercent == null || nowPercent > pointPercent) && (
            <span
              className={classNames(styles.stageLine, secondPartLineClass)}
              style={{
                left: `${pointPercent}%`,
                width: `${
                  nowPercent != null
                    ? nowPercent - pointPercent
                    : ONE_HUNDRED_PERCENT - pointPercent + 1 / ROUND_PERCENT_PARAM
                }%`,
              }}
            />
          )}
        </TimelineStageButton>
        {pointPercent != null && pointBuildAnchor != null && (
          <TimelinePoint
            buildId={buildId}
            name={pointName}
            buildAnchor={pointBuildAnchor}
            logTarget={logTarget}
            type={pointType}
            style={{
              left: `${pointPercent}%`,
            }}
            onMouseEnter={this.onPointMouseEnter}
            onMouseLeave={this.onPointMouseLeave}
          />
        )}
        {viewPointPercent != null && (
          <span
            title="You are here!"
            className={classNames(
              styles.viewPoint,
              pointPercent != null && viewPointPercent >= pointPercent
                ? secondPartLineClass
                : firstPartLineClass,
            )}
            style={{
              left: `${viewPointPercent}%`,
            }}
          />
        )}
      </div>
    )
    return (
      <Dropdown
        anchor={anchor}
        hoverMode
        clickMode={false}
        hoverHideTimeOut={0}
        hoverShowTimeOut={0}
        className={styles.stage}
        style={{
          width: `${durationPercent}%`,
        }}
      >
        <Popup
          className={classNames(styles.popup, {
            [styles.popupHidden]: pointHovered,
          })}
          top={POPUP_TOP_MARGIN}
          left={POPUP_LEFT_MARGIN}
          directions={POPUP_DIRECTIONS}
          trapFocus={false}
        >
          <span className={classNames(styles.fullLabel, labelClass)}>{fullLabel}</span>
        </Popup>
      </Dropdown>
    )
  }
}

export default TimelineStage
